import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from '@material-ui/core/Button'
import { MenuItem, Popover } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import useTranslation from 'Hooks/useTranslation'
import { useAuth } from 'Contexts/AuthProvider'
import FleetDataSignalR from 'Components/SignalrSubscribes/FleetDataSignalR'
import MmsService from 'Services/MMS/mms-api.service'
import AlertToastr from 'utils/alert'
import { isFracFleet, normalizeFleetUnit, normalizeFleetUnits } from '../fleetLiveHelpers'
import { fieldOverrideKey, mergeFleetHistory, normalizeJobPairs } from '../fleetHistoryHelpers'
import FracFleetView from '../components/FracFleetView'
import GenericFleetTable from '../components/GenericFleetTable'
import {
  DEFAULT_FLEET_CHART_DATE_TYPE,
  colorForUnitIndex,
  extractFleetChartUnits,
  getChartOriginMs,
  mergeChartPoints,
} from '../components/fleetChartHelpers'

// A field override the server hasn't confirmed by then is treated as not applied
const PENDING_OVERRIDE_TIMEOUT_MS = 30 * 1000

const FleetDashboard = ({
  departments = [],
  isLoadingDepartments = false,
  token,
  companyId,
}) => {
  const translation = useTranslation()
  const { currentUser } = useAuth()
  const [departmentIdx, setDepartmentIdx] = useState(null)
  const [departmentMenuAnchor, setDepartmentMenuAnchor] = useState(null)
  const [fleetUnits, setFleetUnits] = useState([])
  const [hasReceivedData, setHasReceivedData] = useState(false)
  const [selectedUnitUId, setSelectedUnitUId] = useState(null)
  const [selectedLicenseId, setSelectedLicenseId] = useState(null)
  const [detailUnit, setDetailUnit] = useState(null)
  const [fleetTypeOptions, setFleetTypeOptions] = useState([])
  const [chartRequest, setChartRequest] = useState(null)
  const [chartSeriesByUnit, setChartSeriesByUnit] = useState({})
  const [chartDateOriginMs, setChartDateOriginMs] = useState(null)
  const [chartChannelUnit, setChartChannelUnit] = useState('')
  const [chartFieldName, setChartFieldName] = useState('')
  const [jobPairs, setJobPairs] = useState({})
  const [blenderHistory, setBlenderHistory] = useState({})
  // { [unitUId::fieldName]: { unitUId, fieldName, channelId (null = reset), channelLabel, since } }
  const [pendingFieldChannels, setPendingFieldChannels] = useState({})
  const pendingFieldChannelsRef = useRef({})
  const hubActionsRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    MmsService.getFleetTypesOptions()
      .then((response) => {
        if (!cancelled) setFleetTypeOptions(response?.data || [])
      })
      .catch(() => {
        if (!cancelled) setFleetTypeOptions([])
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!departments.length) {
      if (departmentIdx != null) setDepartmentIdx(null)
      return
    }
    if (departmentIdx == null || departmentIdx > departments.length - 1) {
      setDepartmentIdx(0)
    }
  }, [departments, departmentIdx])

  const department = departmentIdx != null && departments[departmentIdx]
    ? departments[departmentIdx]
    : null

  const fleetType = department?.fleetType || null
  const licenseIds = useMemo(
    () => (department?.licenses || []).map((l) => l.licenseId).filter(Boolean),
    [department]
  )

  const updatePendingFieldChannels = useCallback((updater) => {
    const next = updater(pendingFieldChannelsRef.current)
    pendingFieldChannelsRef.current = next
    setPendingFieldChannels(next)
  }, [])

  const dropPendingFieldChannel = useCallback((key) => {
    updatePendingFieldChannels((prev) => {
      if (!prev[key]) return prev
      const { [key]: _dropped, ...rest } = prev
      return rest
    })
  }, [updatePendingFieldChannels])

  // Overrides are saved for the whole company on the server and applied on the next tick (~10 s)
  const runFieldChannelAction = useCallback((unitUId, fieldName, channelId, channelLabel, action) => {
    if (!unitUId || !fieldName || !action) return
    const key = fieldOverrideKey(unitUId, fieldName)
    updatePendingFieldChannels((prev) => ({
      ...prev,
      [key]: { unitUId, fieldName, channelId, channelLabel, since: Date.now() },
    }))
    action().catch((error) => {
      dropPendingFieldChannel(key)
      AlertToastr.showErrorAlert(error?.message || translation.fleetConnectionErrorLabel || 'Fleet connection failed')
    })
  }, [updatePendingFieldChannels, dropPendingFieldChannel, translation])

  const handleSetFieldChannel = useCallback((unitUId, fieldName, channelId, channelLabel) => {
    if (channelId == null) return
    runFieldChannelAction(unitUId, fieldName, channelId, channelLabel,
      () => hubActionsRef.current?.setFieldChannel(unitUId, fieldName, channelId)
        ?? Promise.reject(new Error('Fleet connection is not ready')))
  }, [runFieldChannelAction])

  const handleResetFieldChannel = useCallback((unitUId, fieldName) => {
    runFieldChannelAction(unitUId, fieldName, null, null,
      () => hubActionsRef.current?.resetFieldChannel(unitUId, fieldName)
        ?? Promise.reject(new Error('Fleet connection is not ready')))
  }, [runFieldChannelAction])

  /** Settle pending overrides against the fields of a fresh message. */
  const settlePendingFieldChannels = useCallback((units) => {
    const pending = pendingFieldChannelsRef.current
    const keys = Object.keys(pending)
    if (!keys.length) return

    const now = Date.now()
    const next = { ...pending }
    keys.forEach((key) => {
      const item = pending[key]
      const unit = units.find((u) => u?.unitUId === item.unitUId)
      const field = (unit?.fields || []).find((f) => f?.fieldName === item.fieldName)
      const applied = item.channelId != null
        ? Boolean(field?.isCustom) && field.channelId === item.channelId
        : Boolean(field) && !field.isCustom
      if (applied) {
        delete next[key]
      } else if (now - item.since > PENDING_OVERRIDE_TIMEOUT_MS) {
        delete next[key]
        if (item.channelId != null) {
          AlertToastr.showErrorAlert(
            (translation.fleetChannelNotAppliedLabel || 'Channel "{channel}" was not applied to {field}')
              .replace('{channel}', item.channelLabel || item.channelId)
              .replace('{field}', item.fieldName)
          )
        }
      }
    })
    if (Object.keys(next).length !== keys.length) {
      pendingFieldChannelsRef.current = next
      setPendingFieldChannels(next)
    }
  }, [translation])

  // A new subscription loads the saved overrides itself — nothing to wait for anymore
  const onSubscribed = useCallback(() => {
    updatePendingFieldChannels(() => ({}))
  }, [updatePendingFieldChannels])

  const subscriptionReady = Boolean(
    department && fleetType && licenseIds.length && token
  )

  // The hub sends one pair per license; none with a pump, blender or MPC job means there is nothing to show
  const pairList = Object.values(jobPairs)
  const hasNoJobs = hasReceivedData && pairList.length > 0
    && pairList.every((pair) => pair.pumpJobId == null && pair.blenderJobId == null && pair.mpcJobId == null)
  const hasPumpJob = pairList.some((pair) => pair.pumpJobId != null)

  useEffect(() => {
    setFleetUnits([])
    setHasReceivedData(false)
    setSelectedUnitUId(null)
    setSelectedLicenseId(null)
    setDetailUnit(null)
    setChartRequest(null)
    setChartSeriesByUnit({})
    setChartDateOriginMs(null)
    setChartChannelUnit('')
    setChartFieldName('')
    setJobPairs({})
    setBlenderHistory({})
    pendingFieldChannelsRef.current = {}
    setPendingFieldChannels({})
  }, [department?.id])

  useEffect(() => {
    // Same empty-state logo as Jobs: body.dashboard + background-logo on .main
    if (!department || hasNoJobs) {
      document.body.classList.add('dashboard')
    } else {
      document.body.classList.remove('dashboard')
    }
    return () => {
      document.body.classList.remove('dashboard')
    }
  }, [department, hasNoJobs])

  const onIncomingData = useCallback((payload) => {
    setHasReceivedData(true)
    // `units` is always the full current list (it can switch to a new job without reconnect)
    const units = normalizeFleetUnits(payload)
    setFleetUnits(units)
    if (payload && !Array.isArray(payload)) {
      setJobPairs(normalizeJobPairs(payload))
      setBlenderHistory((prev) => mergeFleetHistory(prev, payload))
    }
    settlePendingFieldChannels(units)
  }, [settlePendingFieldChannels])

  const onDetailData = useCallback((payload) => {
    const normalized = normalizeFleetUnit(payload)
    setDetailUnit(normalized)
  }, [])

  const handleSelectUnit = useCallback((unitUId, licenseId) => {
    setSelectedUnitUId((prev) => {
      if (prev === unitUId) {
        setSelectedLicenseId(null)
        return null
      }
      setSelectedLicenseId(licenseId || null)
      return unitUId
    })
    setDetailUnit(null)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedUnitUId(null)
    setSelectedLicenseId(null)
    setDetailUnit(null)
  }, [])

  const resetChartSeries = useCallback(() => {
    setChartSeriesByUnit({})
    setChartChannelUnit('')
    setChartFieldName('')
  }, [])

  const handleOpenFleetChart = useCallback((standardChannelName, dateType) => {
    if (!standardChannelName) return
    const resolvedDateType = dateType || chartRequest?.dateType || DEFAULT_FLEET_CHART_DATE_TYPE
    handleCloseDetail()
    resetChartSeries()
    setChartDateOriginMs(getChartOriginMs(resolvedDateType))
    setChartRequest({
      standardChannelName,
      dateType: resolvedDateType,
    })
  }, [chartRequest?.dateType, handleCloseDetail, resetChartSeries])

  const handleCloseFleetChart = useCallback(() => {
    setChartRequest(null)
    setChartDateOriginMs(null)
    resetChartSeries()
  }, [resetChartSeries])

  const handleChartChannelChange = useCallback((standardChannelName) => {
    if (!standardChannelName) return
    const dateType = chartRequest?.dateType || DEFAULT_FLEET_CHART_DATE_TYPE
    resetChartSeries()
    setChartDateOriginMs(getChartOriginMs(dateType))
    setChartRequest({ standardChannelName, dateType })
  }, [chartRequest?.dateType, resetChartSeries])

  const handleChartDateTypeChange = useCallback((dateType) => {
    if (!dateType || !chartRequest?.standardChannelName) return
    resetChartSeries()
    setChartDateOriginMs(getChartOriginMs(dateType))
    setChartRequest((prev) => (prev ? { ...prev, dateType } : prev))
  }, [chartRequest?.standardChannelName, resetChartSeries])

  const onChartData = useCallback((payload) => {
    const units = extractFleetChartUnits(payload)
    const chartData = units.map((unit) => ({
      unitUId: unit.unitUId,
      unitName: unit.unitName,
      fieldName: unit.fieldName,
      channelName: unit.channelName,
      channelUnit: unit.channelUnit,
      pointsCount: unit.data?.length || 0,
      data: unit.data,
    }))

    if (!units.length) return

    setChartSeriesByUnit((prev) => {
      const next = { ...prev }
      let colorIndex = Object.keys(next).length

      units.forEach((unit) => {
        const existing = next[unit.unitUId]
        const color = existing?.color || colorForUnitIndex(colorIndex)
        if (!existing) colorIndex += 1

        next[unit.unitUId] = {
          unitUId: unit.unitUId,
          unitName: unit.unitName || existing?.unitName || unit.unitUId,
          licenseId: unit.licenseId || existing?.licenseId || '',
          fieldName: unit.fieldName || existing?.fieldName || '',
          channelName: unit.channelName || existing?.channelName || '',
          channelUnit: unit.channelUnit || existing?.channelUnit || '',
          color,
          points: mergeChartPoints(
            existing?.points,
            unit.data,
            chartRequest?.dateType || DEFAULT_FLEET_CHART_DATE_TYPE,
          ),
        }
      })
      return next
    })

    const firstWithMeta = units.find((u) => u.channelUnit || u.fieldName)
    if (firstWithMeta?.channelUnit) {
      setChartChannelUnit((prev) => prev || firstWithMeta.channelUnit)
    }
    if (firstWithMeta?.fieldName) {
      setChartFieldName((prev) => prev || firstWithMeta.fieldName)
    }
  }, [chartRequest])

  const onConnectionError = useCallback((error) => {
    const message = error?.message || translation.fleetConnectionErrorLabel || 'Fleet connection failed'
    AlertToastr.showErrorAlert(message)
  }, [translation])

  const getFleetTypeName = useCallback((fleetTypeValue) => {
    if (!fleetTypeValue) return ''
    const key = String(fleetTypeValue)
    const option = fleetTypeOptions.find((item) => (
      String(item?.fleetType || '') === key
      || String(item?.fleetType || '').toLowerCase() === key.toLowerCase()
    ))
    return option?.name || key
  }, [fleetTypeOptions])

  const departmentLabel = useCallback((dept) => {
    if (!dept) {
      if (isLoadingDepartments) return translation.fleetLoadingLabel || 'Loading…'
      return translation.fleetViewSelectFleetLabel || 'Select fleet'
    }
    return dept.name || dept.label || (translation.fleetViewSelectFleetLabel || 'Select fleet')
  }, [isLoadingDepartments, translation])

  const getDepartmentFleetTypeName = useCallback((dept) => {
    if (!dept) return ''
    return getFleetTypeName(dept.fleetTypeName || dept.fleetType)
  }, [getFleetTypeName])

  const getDepartmentLicensesList = useCallback((dept) => {
    if (!dept) return ''
    return (dept.licenses || [])
      .map((license) => license?.licenseName || license?.name || '')
      .map((name) => String(name).trim())
      .filter(Boolean)
      .join(', ')
  }, [])

  const departmentSubtitle = (dept) => {
    if (!dept) return ''
    if (dept.subtitle) return dept.subtitle
    return [getDepartmentFleetTypeName(dept), getDepartmentLicensesList(dept)]
      .filter(Boolean)
      .join(' · ')
  }

  const selectedDepartmentMeta = useMemo(() => {
    if (!department) return null
    return {
      name: departmentLabel(department),
      fleetType: getDepartmentFleetTypeName(department),
    }
  }, [department, departmentLabel, getDepartmentFleetTypeName])

  return (
    <div className="fleetViewShell">
      <div className="pageBtnSection">
        <div className="part" aria-hidden="true" />
        <div className="part centered">
          <Button
            className={`fleetViewDeptBtn${department ? ' fleetViewDeptBtn--selected' : ''}`}
            endIcon={<ArrowDropDownIcon />}
            id="fleetViewSelectFleetBtn"
            onClick={(e) => setDepartmentMenuAnchor(e.currentTarget)}
            disabled={!departments.length && !isLoadingDepartments}
          >
            {selectedDepartmentMeta ? (
              <span className="fleetViewDeptBtnContent">
                <span className="fleetViewDeptBtnName">{selectedDepartmentMeta.name}</span>
                {selectedDepartmentMeta.fleetType ? (
                  <>
                    <span className="fleetViewDeptBtnSep" aria-hidden="true">·</span>
                    <span className="fleetViewDeptBtnMeta">{selectedDepartmentMeta.fleetType}</span>
                  </>
                ) : null}
              </span>
            ) : (
              <span>{departmentLabel(null)}</span>
            )}
          </Button>
          <Popover
            open={Boolean(departmentMenuAnchor)}
            anchorEl={departmentMenuAnchor}
            onClose={() => setDepartmentMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            disableScrollLock
            PaperProps={{ className: 'fleetViewDeptPopover' }}
          >
            {departments.length ? (
              departments.map((dept, idx) => (
                <MenuItem
                  key={dept.id || idx}
                  className="filterMenuItem fleetViewDeptMenuItem"
                  selected={departmentIdx != null && idx === departmentIdx}
                  onClick={() => {
                    setDepartmentIdx(idx)
                    setDepartmentMenuAnchor(null)
                  }}
                  id={`fleetViewSelectFleet-${dept.id || idx}`}
                >
                  <div className="fleetViewDeptMenuItemContent">
                    <div className="fleetViewDeptMenuItemName">{departmentLabel(dept)}</div>
                    {departmentSubtitle(dept) ? (
                      <div className="fleetViewDeptSubtitle">
                        {departmentSubtitle(dept)}
                      </div>
                    ) : null}
                  </div>
                </MenuItem>
              ))
            ) : (
              <MenuItem className="filterMenuItem" disabled>
                {isLoadingDepartments
                  ? (translation.fleetLoadingLabel || 'Loading…')
                  : (translation.fleetNoDepartmentsLabel || 'No departments')}
              </MenuItem>
            )}
          </Popover>
        </div>
        <div className="part end" aria-hidden="true" />
      </div>

      {subscriptionReady ? (
        <FleetDataSignalR
          key={`${department.id}:${fleetType}:${licenseIds.join(',')}`}
          token={token}
          companyId={companyId}
          fleetType={fleetType}
          licenseIds={licenseIds}
          selectedUnitUId={selectedUnitUId}
          selectedLicenseId={selectedLicenseId}
          chartRequest={chartRequest}
          hubActionsRef={hubActionsRef}
          onSubscribed={onSubscribed}
          onIncomingData={onIncomingData}
          onDetailData={onDetailData}
          onChartData={onChartData}
          onConnectionError={onConnectionError}
        />
      ) : null}

      {department && !hasNoJobs ? (
        <div className="fleetDashboard">
          {!subscriptionReady ? (
            <div className="pageBlock">
              <div className="pageCard">
                <div className="fleetEmptyState">
                  {translation.fleetMissingSubscriptionLabel
                    || 'Selected fleet has no fleet type or licenses'}
                </div>
              </div>
            </div>
          ) : isFracFleet(fleetType) ? (
            <FracFleetView
              units={fleetUnits}
              hasReceivedData={hasReceivedData}
              hasPumpJob={hasPumpJob || !pairList.length}
              allSources={!!currentUser?.isFleetViewAllSources?.()}
              selectedUnitUId={selectedUnitUId}
              detailUnit={detailUnit}
              onSelectUnit={handleSelectUnit}
              onCloseDetail={handleCloseDetail}
              fleetType={fleetType}
              licenseIds={licenseIds}
              chartRequest={chartRequest}
              chartSeriesByUnit={chartSeriesByUnit}
              chartDateOriginMs={chartDateOriginMs}
              chartChannelUnit={chartChannelUnit}
              chartFieldName={chartFieldName}
              onOpenFleetChart={handleOpenFleetChart}
              onCloseFleetChart={handleCloseFleetChart}
              onChartChannelChange={handleChartChannelChange}
              onChartDateTypeChange={handleChartDateTypeChange}
              blenderHistory={blenderHistory}
              jobPairs={jobPairs}
              pendingFieldChannels={pendingFieldChannels}
              onSetFieldChannel={handleSetFieldChannel}
              onResetFieldChannel={handleResetFieldChannel}
              translation={translation}
            />
          ) : (
            <div className="pageBlock">
              <div className="pageCard" style={{ padding: 12 }}>
                {!hasReceivedData ? (
                  <div className="fleetEmptyState">
                    {translation.fleetWaitingForDataLabel || 'Waiting for data…'}
                  </div>
                ) : (
                  <GenericFleetTable
                    unbillableLabel={translation.fleetUnitNotBillableLabel}
                    units={fleetUnits}
                    selectedUnitUId={selectedUnitUId}
                    detailUnit={detailUnit}
                    onSelectUnit={handleSelectUnit}
                    onCloseDetail={handleCloseDetail}
                    waitingLabel={translation.fleetWaitingForDataLabel || 'Waiting for data…'}
                    mappingsHint={translation.fleetNoChannelMappingsHint || 'No channel mappings initialized'}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default FleetDashboard
